'use client'

import React, { FC, useMemo } from 'react'
import classNames from 'classnames'

/**
 * Deterministic pseudo-random in [0, 1) from a seed, so a tile always shows
 * the same placeholder (no re-shuffle on re-render, SSR and client agree).
 */
function mulberry32(seed: number) {
	let a = seed >>> 0
	return () => {
		a = (a + 0x6d2b79f5) >>> 0
		let t = a
		t = Math.imul(t ^ (t >>> 15), t | 1)
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
}

type Square = { left: number; top: number; size: number; delay: number; duration: number }

/**
 * What a bitmap tile shows before its data has arrived: a handful of dim
 * orange squares scattered on the tile's dark ground, each softly blinking on
 * its own beat. It is the same shape as the finished bitmap (squares on a
 * grid), so the swap to the real art reads as "filled in" rather than
 * "replaced", and it is pure CSS so 40 of them cost nothing.
 */
const BitmapPlaceholder: FC<{ seed: number; className?: string; count?: number }> = ({
	seed,
	className,
	count = 14
}) => {
	const squares = useMemo<Square[]>(() => {
		const rnd = mulberry32(seed || 1)
		return Array.from({ length: count }, () => {
			const size = 3 + Math.floor(rnd() * 3) * 2 // 3 | 5 | 7 % of the tile
			return {
				left: 4 + rnd() * (92 - size),
				top: 4 + rnd() * (92 - size),
				size,
				delay: -rnd() * 2.4,
				duration: 1.6 + rnd() * 1.4
			}
		})
	}, [seed, count])

	return (
		<div
			aria-hidden
			className={classNames('pointer-events-none absolute inset-0 overflow-hidden', className)}
		>
			{squares.map((s, i) => (
				<span
					key={i}
					className="absolute block rounded-[1px] bg-[rgba(247,147,26,0.5)] motion-safe:animate-bitmap-blink"
					style={{
						left: `${s.left}%`,
						top: `${s.top}%`,
						width: `${s.size}%`,
						height: `${s.size}%`,
						animationDelay: `${s.delay}s`,
						animationDuration: `${s.duration}s`
					}}
				/>
			))}
		</div>
	)
}

export default BitmapPlaceholder
